import AppModal from "components/Modal";
import React, { useState, useEffect } from 'react';
import ReactDOM from "react-dom";
// import { useAuth } from '../../../helpers/authContext';
import { Button, Card, CardBody, Col, FormGroup, Input, Row } from "reactstrap";
import { useToast } from "components/ui/use-toast";

type Props = {
    isShowing: boolean;
    hide: () => void;
    initialOperation?: string;
    initialCurrency?: string;
};

export const GenerateLinkModal = ({ isShowing, hide, initialOperation = "transfer", initialCurrency = "STEEM" }: Props) => {
    // const { user } = useAuth();
    const { toast } = useToast();
    const [operation, setOperation] = useState(initialOperation);
    const [to, setTo] = useState("");
    const [amount, setAmount] = useState<number | "">("");
    const [currency, setCurrency] = useState(initialCurrency);
    const [memo, setMemo] = useState("");
    const [generatedLink, setGeneratedLink] = useState("");

    useEffect(() => {
        if (isShowing) {
            setOperation(initialOperation);
            setCurrency(initialCurrency);
            setTo("");
            setAmount("");
            setMemo("");
            setGeneratedLink("");
        }
    }, [isShowing, initialOperation, initialCurrency]);

    const handleGenerate = () => {
        if (!to || !amount) {
            toast({
                title: "Incomplete fields",
                description: "Please specify a recipient and an amount.",
                variant: "rose_red"
            });
            return;
        }

        const baseUrl = window.location.origin;
        const params = new URLSearchParams({
            op: operation,
            to: to,
            amount: amount.toString(),
            currency: currency
        });

        if (operation === "transfer" && memo) {
            params.append("memo", memo);
        }

        const link = `${baseUrl}/wallet?${params.toString()}`;
        setGeneratedLink(link);
    };

    const handleCopy = () => {
        if (generatedLink) {
            navigator.clipboard.writeText(generatedLink);
            toast({
                title: "Link Copied",
                description: "The link has been copied to your clipboard.",
                variant: "wild_green"
            });
        }
    };

    const renderForm = (
        <div className="modal-body post-editor-body p-0">
            <Card className="no-shadow border-0">
                <CardBody className="px-lg-3 py-lg-3 body-gray">
                    <Row>
                        <Col md="12" className="mb-3">
                            <p className="promotion-description">
                                Generate a shareable link to easily request an operation. Anyone clicking this link will be prompted to confirm the action.
                            </p>
                        </Col>
                        <Col md="12" className="mb-3">
                            <label className="text-muted small">Operation Type</label>
                            <Input type="select" className="form-control fancy-input" value={operation} onChange={(e) => setOperation(e.target.value)}>
                                <option value="transfer">Transfer (Send Funds)</option>
                                <option value="transferToVesting">Power Up</option>
                                <option value="delegateVestingShares">Delegate</option>
                            </Input>
                        </Col>
                        
                        <Col md="12" className="mb-3">
                            <FormGroup>
                                <div className="input-icon-group">
                                    <i className="fas fa-user input-icon" />
                                    <input type="text" className="form-control fancy-input" placeholder="Recipient" value={to} onChange={(e) => setTo(e.target.value)} />
                                </div>
                            </FormGroup>
                        </Col>
                        <Col className="col-8" md="8">
                            <FormGroup>
                                <div className="input-icon-group">
                                    <i className="fas fa-coins input-icon" />
                                    <input type="number" className="form-control fancy-input" placeholder="Amount" value={amount} onChange={(e) => setAmount(parseFloat(e.target.value))} />
                                </div>
                            </FormGroup>
                        </Col>
                        <Col className="col-4" md="4">
                            <Input type="select" className="form-control fancy-input" value={currency} onChange={(e) => setCurrency(e.target.value)} disabled={operation === "delegateVestingShares"}>
                                <option value="STEEM">STEEM</option>
                                {operation === "transfer" && <option value="SBD">SBD</option>}
                                {(operation === "transferToVesting" || operation === "delegateVestingShares") && <option value="SP">SP</option>}
                            </Input>
                        </Col>
                        
                        {operation === "transfer" && (
                            <Col md="12" className="mb-3">
                                <FormGroup>
                                    <div className="input-icon-group">
                                        <i className="fas fa-comment-dots input-icon" />
                                        <input type="text" className="form-control fancy-input" placeholder="Memo (Optional)" value={memo} onChange={(e) => setMemo(e.target.value)} />
                                    </div>
                                </FormGroup>
                            </Col>
                        )}

                        <Col md="12" className="mt-2">
                            <Button color="default" className="w-100 btn-fancy" onClick={handleGenerate}>
                                Generate Link <i className="fas fa-magic ml-2" />
                            </Button>
                        </Col>

                        {generatedLink && (
                            <Col md="12" className="mt-4">
                                <label className="text-muted small">Generated Link</label>
                                <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                                    <input type="text" className="form-control fancy-input" value={generatedLink} readOnly />
                                    <Button color="primary" onClick={handleCopy} title="Copy to clipboard">
                                        <i className="fas fa-copy" />
                                    </Button>
                                </div>
                            </Col>
                        )}
                    </Row>
                </CardBody>
            </Card>
        </div>
    );

    return isShowing ? ReactDOM.createPortal(
        <React.Fragment>
            <AppModal
                isShowing={isShowing}
                hide={hide}
                size="md"
                header="Generate Operation Link"
            >
                {renderForm}
            </AppModal>
        </React.Fragment>, document.body
    ) : null;
};

export default GenerateLinkModal;
